import {
    StyledAgendaGenericEventContent,
    StyledAgendaGenericEventParagraph,
    StyledAgendaGenericEventSubParagraph,
    StyledAgendaGenericEventTitle
} from '@/features/agenda/components/agenda-generic-event/style'
import dayjs from 'dayjs'
import type {AgendaEventGeneric} from '@/features/agenda/types'
import {forwardRef} from 'react'
import {useTranslation} from '@/translations/i18n'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {
    StyledAgendaAppointmentEvent,
    StyledAgendaAppointmentEventStatusIndicator
} from '../agenda-appointment-event/style'

interface AgendaGenericEventProps {
    start: Date
    end: Date
    event: AgendaEventGeneric
    isSmall?: boolean
    onClick?: () => void
}

export const AgendaGenericEvent = forwardRef<HTMLDivElement, AgendaGenericEventProps>(
    ({start, end, event, isSmall = false, onClick}, ref) => {
        return (
            <StyledAgendaAppointmentEvent $status={event.status} onClick={onClick} ref={ref}>
                <StyledAgendaAppointmentEventStatusIndicator $status={event.status} />
                <StyledAgendaGenericEventContent direction="column">
                    {isSmall ? (
                        <SmallAgendaGenericEventContent start={start} end={end} event={event} />
                    ) : (
                        <RegularAgendaGenericEventContent start={start} end={end} event={event} />
                    )}
                </StyledAgendaGenericEventContent>
            </StyledAgendaAppointmentEvent>
        )
    }
)

const SmallAgendaGenericEventContent = ({start, end, event}: {start: Date; end: Date; event: AgendaEventGeneric}) => {
    const {t} = useTranslation()
    const title = event.note || t('agenda:appointment_type_generic_event')
    const consultantName = event.eventConsultants?.[0]?.user?.fullName ?? t('agenda:appointment_type_generic_event')

    const content = (
        <>
            <StyledAgendaGenericEventTitle>{title}</StyledAgendaGenericEventTitle>
            <StyledAgendaGenericEventParagraph>
                <span>{consultantName}</span>
            </StyledAgendaGenericEventParagraph>
            <StyledAgendaGenericEventSubParagraph>
                {dayjs(start).format('HH:mm')} - {dayjs(end).format('HH:mm')}
            </StyledAgendaGenericEventSubParagraph>
        </>
    )

    return (
        <Tooltip content={content} variant="light" isOpenOnClickEnabled={false}>
            <StyledAgendaGenericEventTitle>{title}</StyledAgendaGenericEventTitle>
        </Tooltip>
    )
}

const RegularAgendaGenericEventContent = ({start, end, event}: {start: Date; end: Date; event: AgendaEventGeneric}) => {
    const {t} = useTranslation()
    const title = event.note || t('agenda:appointment_type_generic_event')
    const consultantName = event.eventConsultants?.[0]?.user?.fullName ?? t('agenda:appointment_type_generic_event')

    return (
        <>
            <StyledAgendaGenericEventTitle>{title}</StyledAgendaGenericEventTitle>
            <StyledAgendaGenericEventParagraph>
                <span>{consultantName}</span>
            </StyledAgendaGenericEventParagraph>
            <StyledAgendaGenericEventSubParagraph>
                {event.time && event.time != '00:00:00'
                    ? `${dayjs(start).format('HH:mm')} - ${dayjs(end).format('HH:mm')}`
                    : t('agenda:generated_event')}
            </StyledAgendaGenericEventSubParagraph>
        </>
    )
}
